import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { lastValueFrom } from 'rxjs';
import { AuthService } from '../../../auth/services/auth.service';
import { BanksService } from '../../../banks/services/banks.service';
import { RegionsService } from '../../../banks/services/regions.service';
import { BankSummaryDto } from '../../../../core/data/new-dtos/bank.dtos';
import { RegionDto } from '../../../../core/data/new-dtos/region.dtos';
import {
  BankDirectorDto,
  CreateBankDirectorDto,
} from '../../../../core/data/new-dtos/bank-director.dtos';

export interface DirectorInviteModalResult {
  action: 'save' | 'cancel';
  invite?: CreateBankDirectorDto;
  regionIds?: number[];
}

@Component({
  selector: 'app-director-invite-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './director-invite-modal.component.html',
})
export class DirectorInviteModalComponent implements OnInit, AfterViewInit, OnDestroy {
  /** When set, the modal only edits this regional user's region assignments. */
  @Input() editingRegionsFor?: BankDirectorDto;
  @Output() closed = new EventEmitter<DirectorInviteModalResult>();

  form!: FormGroup;
  banks: BankSummaryDto[] = [];
  regions: RegionDto[] = [];
  selectedRegionIds = new Set<number>();
  loadingRegions = false;

  constructor(
    private fb: FormBuilder,
    private auth: AuthService,
    private banksService: BanksService,
    private regionsService: RegionsService,
    private elementRef: ElementRef,
  ) { }

  get isSuperUser() {
    return this.auth.isSuperUser;
  }

  get isRegionsOnly() {
    return !!this.editingRegionsFor;
  }

  get roleValue(): string {
    return this.isRegionsOnly ? 'Regional' : this.form.get('role')!.value;
  }

  get needsRegions(): boolean {
    return this.roleValue === 'Regional';
  }

  get canSave(): boolean {
    if (this.isRegionsOnly) return this.selectedRegionIds.size > 0;
    if (this.form.invalid) return false;
    return !this.needsRegions || this.selectedRegionIds.size > 0;
  }

  async ngOnInit() {
    // Superusers seed a bank's first global admin; globals invite within their bank
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      name: ['', Validators.required],
      lastName: ['', Validators.required],
      bankId: [this.auth.bankProfile?.bankId ?? null, Validators.required],
      role: [{ value: 'Global', disabled: this.isSuperUser }, Validators.required],
    });

    if (this.isRegionsOnly) {
      this.selectedRegionIds = new Set(this.editingRegionsFor!.regions.map((r) => r.id));
      await this.loadRegions(this.editingRegionsFor!.bankId);
      return;
    }

    if (this.isSuperUser) {
      try {
        this.banks = await lastValueFrom(this.banksService.getBanks());
      } catch {
        this.banks = [];
      }
      return;
    }

    // A global caller may invite regionals, which need the bank's regions
    const bankId = this.auth.bankProfile?.bankId;
    if (bankId) {
      await this.loadRegions(bankId);
    }
  }

  ngAfterViewInit(): void {
    // Escapes any stacking or overflow context around the trigger
    document.body.appendChild(this.elementRef.nativeElement);
  }

  ngOnDestroy(): void {
    const element = this.elementRef.nativeElement;
    if (element?.parentNode === document.body) {
      document.body.removeChild(element);
    }
  }

  toggleRegion(regionId: number, checked: boolean) {
    if (checked) {
      this.selectedRegionIds.add(regionId);
    } else {
      this.selectedRegionIds.delete(regionId);
    }
  }

  onRegionToggled(regionId: number, event: Event) {
    this.toggleRegion(regionId, (event.target as HTMLInputElement).checked);
  }

  onCancel() {
    this.closed.emit({ action: 'cancel' });
  }

  onSave() {
    if (!this.canSave) return;

    if (this.isRegionsOnly) {
      this.closed.emit({ action: 'save', regionIds: [...this.selectedRegionIds] });
      return;
    }

    const value = this.form.getRawValue();
    this.closed.emit({
      action: 'save',
      invite: {
        email: value.email,
        name: value.name,
        lastName: value.lastName,
        bankId: Number(value.bankId),
        role: value.role,
        regionIds: this.needsRegions ? [...this.selectedRegionIds] : [],
      },
    });
  }

  onBackdropClick(event: MouseEvent) {
    if ((event.target as HTMLElement).classList.contains('modal')) {
      this.onCancel();
    }
  }

  private async loadRegions(bankId: number) {
    this.loadingRegions = true;
    try {
      this.regions = await lastValueFrom(this.regionsService.getRegions(bankId));
    } catch {
      this.regions = [];
    } finally {
      this.loadingRegions = false;
    }
  }
}
